import { declineModalAtom, endModalAtom, closeVideoAtom } from '../../../video/atoms';
import { useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCurrentUser } from '@bizzabo/entities';
import { useSocket } from '../../../contexts/Socket';
import { incomingCallAtom, IncomingNotification } from '../../videoNotifications/atoms';
import useVideoEnable from '../../../hooks/useVideoEnable';

const DOMAIN = 'meetings-service';

export const useNotificationSubscribe = (eventId: number, eventUniqueName: string) => {
    const { socket, isConnected } = useSocket();
    const { data: currentUser } = useCurrentUser(eventId);
    const { data: isVideoEnable } = useVideoEnable(eventUniqueName);

    const [incomingCall, setIncomingCallAtom] = useAtom(incomingCallAtom);
    const declineModal = useAtomValue(declineModalAtom);
    const endModal = useAtomValue(endModalAtom);
    const closeVideo = useSetAtom(closeVideoAtom);

    useEffect(() => {
        let incomingCallSubscriber = () => {};

        const subscribe = async () => {
            incomingCallSubscriber = await socket.subscribe({
                domain: DOMAIN,
                event: 'incoming_call',
                entityId: currentUser.id,
                cb: async (meeting: IncomingNotification) => {
                    if (declineModal.isOpen || endModal.isOpen) {
                        closeVideo();
                    }
                    setIncomingCallAtom({ hasIncomingCall: true, incomingNotification: meeting });
                },
            });
        };

        if (isVideoEnable && currentUser && isConnected) {
            subscribe();
        }

        return () => {
            incomingCallSubscriber();
        };
    }, [socket, isConnected, currentUser, declineModal.isOpen, endModal.isOpen]);

    useEffect(() => {
        let declineSubscriber = () => {};
        let timeoutSubscriber = () => {};
        let endSubscriber = () => {};

        const subscribe = async () => {
            declineSubscriber = await socket.subscribe({
                domain: DOMAIN,
                event: 'call_declined',
                entityId: currentUser.id,
                cb: async () => {
                    stopIncomingCall();
                },
            });

            timeoutSubscriber = await socket.subscribe({
                domain: DOMAIN,
                event: 'call_timeout',
                entityId: currentUser.id,
                cb: async () => {
                    stopIncomingCall();
                },
            });

            endSubscriber = await socket.subscribe({
                domain: DOMAIN,
                event: 'call_ended',
                entityId: currentUser.id,
                cb: async () => {
                    stopIncomingCall();
                },
            });
        };

        if (incomingCall.hasIncomingCall) {
            subscribe();
        } else {
            declineSubscriber();
            timeoutSubscriber();
            endSubscriber();
        }

        return () => {
            declineSubscriber();
            timeoutSubscriber();
            endSubscriber();
        };
    }, [socket, currentUser, incomingCall.hasIncomingCall]);

    const stopIncomingCall = () => {
        setIncomingCallAtom({ hasIncomingCall: false });
    };
};
